"use client";

import { useEstimator } from "../../_context/EstimatorContext";
import { cityCostBars } from "../../_logic/cost-bars";
import { MIN_COST_BARS } from "../../_logic/constants";
import type { CityEstimate } from "../../_logic/types";

interface CityCostBarsProps {
  city: CityEstimate;
  /** Show amounts for the whole group instead of per person. */
  totalMode: boolean;
}

/**
 * Where a city's money goes: one bar per priced category, largest first. The solid part runs to
 * the low end of the range, the pale part on to the high end, all on one scale so bars compare.
 * Hidden before dates are set, and when fewer than two categories are priced, since one bar says
 * nothing a number doesn't.
 * @param props - The city and whether amounts are for the group.
 * @returns The bars, or null.
 * @throws Never.
 */
export const CityCostBars = ({ city, totalMode }: CityCostBarsProps) => {
  // Hooks
  const { workspace, estimate, money } = useEstimator();

  // Derived
  const bars = cityCostBars(city, {
    totalMode,
    headcount: estimate.headcount,
    flightsPriceable: workspace.dates !== null && workspace.origins.length > 0,
  });
  const scale = Math.max(...bars.map((bar) => bar.range.high));
  const percent = (value: number) => `${(value / scale) * 100}%`;

  // Render
  // Without dates the rows below show dashes, so one-day amounts here would mislead.
  if (workspace.dates === null || bars.length < MIN_COST_BARS) return null;
  return (
    <section aria-label={`Where ${city.city}'s cost goes`} className="flex flex-col gap-4 px-4 pt-1 pb-5">
      <h4 className="text-sm font-semibold text-gray-900">Where the cost goes</h4>
      {/* One grid for every row, so labels, bars, amounts and shares line up down the list. */}
      <ul className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-4 @[560px]:grid-cols-[max-content_1fr_max-content_max-content] @[560px]:gap-y-3">
        {bars.map((bar) => (
          <li key={bar.key} className="col-span-3 grid grid-cols-subgrid items-center gap-y-2 @[560px]:col-span-4">
            <span className="text-sm font-medium text-gray-700 @[560px]:whitespace-nowrap">{bar.label}</span>
            <div
              aria-hidden
              className="relative col-span-3 row-start-2 h-2.5 overflow-hidden rounded-full bg-gray-100 @[560px]:col-span-1 @[560px]:col-start-2 @[560px]:row-start-1 @[560px]:mx-2"
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-indigo-100 transition-[width] duration-300 ease-out motion-reduce:transition-none"
                style={{ width: percent(bar.range.high) }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-indigo-300 transition-[width] duration-300 ease-out motion-reduce:transition-none"
                style={{ width: percent(bar.range.low) }}
              />
            </div>
            <span className="whitespace-nowrap text-right text-sm font-medium text-gray-900 tabular-nums">
              {money.formatRange(bar.range, totalMode)}
            </span>
            <span className="w-9 text-right text-xs font-medium text-gray-600 tabular-nums">
              {Math.round(bar.share * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
